import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import React, { useCallback, useEffect, useState } from 'react'

import { fetchIssueCategories, updateIssueCategory } from 'api'
import { fetchRoleHolders } from 'api/roles'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

/**
 * Issue Owners — who each of the 13 Issue Tracker categories routes to.
 *
 * The API to edit this shipped with the tracker; nothing called it, so a
 * handover or a missing Slack id meant a data migration and a deploy. That is
 * the whole reason this page exists: the routing table is operational config,
 * not schema.
 *
 * A blank Slack id is a supported state, not an error — the message names the
 * owner in plain text instead of tagging them. The banner counts them so the
 * gap is visible without being a blocker.
 */
const useStyles = makeStyles((theme) => ({
  // Geometry matches Operations/IncidentReports and the Issue Tracker board so
  // the three read as one product.
  root: { padding: theme.spacing(3) },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  banner: {
    background: '#fbf0dc',
    color: '#a4580a',
    borderRadius: 7,
    padding: theme.spacing(1.25, 1.5),
    fontSize: 14,
    marginBottom: theme.spacing(2)
  },
  bannerOk: { background: '#e3f2e7', color: '#196b3a' },
  cat: { fontWeight: 600 },
  sub: { color: '#8b837a', fontSize: 12.5, display: 'block' },
  missing: { color: '#a4580a', fontWeight: 600, fontSize: 12.5 },
  field: { minWidth: 150 },
  empty: { padding: theme.spacing(6), textAlign: 'center', color: '#8b837a' },
  // The category name is long enough to stretch the row; let it wrap. Alfred's
  // table styling sets white-space:nowrap, which word-break cannot beat.
  wrap: { whiteSpace: 'normal', wordBreak: 'break-word' },
  colCat: { width: '28%' },
  colOwner: { width: '19%' },
  colSlack: { width: '19%' },
  colAccount: { width: '34%' }
}))

const IssueOwners = () => {
  const classes = useStyles()

  const [rows, setRows] = useState([])
  const [draft, setDraft] = useState({})
  // Alfred accounts, for the Resolved-by link. Admins only: the owner of a
  // category is somebody who works the board, and the full role-holder list is
  // ~1.8k rows once teachers are in it.
  const [people, setPeople] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [sopOpen, setSopOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchIssueCategories()
      // Retired categories (active=false — e.g. the second Finance, folded
      // into one on 2026-09-25) are not owned by anyone any more; listing
      // them showed "two Finance, both Madhurya" (Satya, 2026-09-28).
      const list = (Array.isArray(data) ? data : []).filter(
        (row) => row.active !== false
      )
      setRows(list)
      setDraft(
        list.reduce(
          (acc, row) => ({
            ...acc,
            [row.uuid]: {
              owner_name: row.owner_name || '',
              owner_slack_id: row.owner_slack_id || '',
              owner_user: row.owner_user || ''
            }
          }),
          {}
        )
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const loadPeople = async () => {
      try {
        const all = await fetchRoleHolders()
        setPeople(
          all
            .filter(
              (person) =>
                (person.kinds || []).includes('Admin') && person.enabled
            )
            .map((person) => ({
              uuid: person.uuid,
              // Several people hold more than one account with the same name
              // (four "Manjushree Mahadev" rows on dev), so the email is part
              // of the label — often the only thing that tells them apart.
              label: [person.name, person.email].filter(Boolean).join(' · ')
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      } catch (error) {
        // A missing picker list must not take the page down: the Slack id and
        // owner name still edit fine without it.
        setPeople([])
      }
    }
    loadPeople()
  }, [])

  const set = (uuid, key) => (value) =>
    setDraft((prev) => ({ ...prev, [uuid]: { ...prev[uuid], [key]: value } }))

  /**
   * Manjushree owns four categories and Ekta two, so the same id would be typed
   * four times — which is how the four end up disagreeing. Entering it once
   * fills the owner's other blank rows.
   *
   * Only BLANK rows are filled: a category deliberately pointed at a different
   * id must not be overwritten by editing a sibling.
   */
  const applyToSameOwner = (uuid) => {
    const source = draft[uuid]
    const owner = (source?.owner_name || '').trim().toLowerCase()
    const slackId = (source?.owner_slack_id || '').trim()
    if (!owner || !slackId) return

    const targets = rows.filter((row) => {
      if (row.uuid === uuid) return false
      const candidate = draft[row.uuid]
      return (
        (candidate?.owner_name || '').trim().toLowerCase() === owner &&
        !(candidate?.owner_slack_id || '').trim()
      )
    })
    if (!targets.length) return

    setDraft((prev) =>
      targets.reduce(
        (acc, row) => ({
          ...acc,
          [row.uuid]: { ...acc[row.uuid], owner_slack_id: slackId }
        }),
        prev
      )
    )
    showToast(
      `Also filled ${targets.length} other ${
        targets.length === 1 ? 'category' : 'categories'
      } owned by ${source.owner_name}.`,
      constants.SUCCESS
    )
  }

  const changed = rows.filter((row) => {
    const next = draft[row.uuid]
    if (!next) return false
    return (
      next.owner_name !== (row.owner_name || '') ||
      next.owner_slack_id !== (row.owner_slack_id || '') ||
      next.owner_user !== (row.owner_user || '')
    )
  })

  const save = async () => {
    setSaving(true)
    try {
      // One PATCH per changed row — the endpoint is per-category, and a partial
      // failure leaves the rows that did save saved rather than rolling back a
      // batch nobody can see.
      await Promise.all(
        changed.map((row) =>
          updateIssueCategory(row.uuid, {
            owner_name: (draft[row.uuid].owner_name || '').trim(),
            owner_slack_id: (draft[row.uuid].owner_slack_id || '').trim(),
            owner_user: draft[row.uuid].owner_user || ''
          })
        )
      )
      showToast(
        `Saved ${changed.length} ${
          changed.length === 1 ? 'category' : 'categories'
        }.`,
        constants.SUCCESS
      )
      await load()
    } catch (error) {
      showToast(
        'Could not save. Nothing was lost — try again.',
        constants.ERROR
      )
    } finally {
      setSaving(false)
    }
  }

  const missing = rows.filter(
    (row) => !(draft[row.uuid]?.owner_slack_id || '').trim()
  ).length

  // Separate count, separate consequence: a blank Slack id costs you an
  // @-mention, a blank Alfred account costs you the resolve gate entirely.
  const unclaimed = rows.filter(
    (row) => !(draft[row.uuid]?.owner_user || '')
  ).length

  return (
    <div className={classes.root}>
      <Box className={classes.head}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5">Issue Owners</Typography>
          <Tooltip title="how this works">
            <IconButton size="small" onClick={() => setSopOpen(true)}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Button
          variant="contained"
          color="primary"
          disabled={!changed.length || saving}
          onClick={save}
        >
          {saving
            ? 'Saving…'
            : `Save changes${changed.length ? ` (${changed.length})` : ''}`}
        </Button>
      </Box>

      {!loading && rows.length ? (
        <div className={`${classes.banner} ${missing ? '' : classes.bannerOk}`}>
          {missing
            ? `${missing} of ${rows.length} categories have no Slack id. Those issues still
               route and still post — the message names the owner in plain text instead of
               tagging them.`
            : `All ${rows.length} categories have a Slack id.`}
        </div>
      ) : null}

      {!loading && rows.length ? (
        <div
          className={`${classes.banner} ${unclaimed ? '' : classes.bannerOk}`}
        >
          {unclaimed
            ? `${unclaimed} of ${rows.length} categories have no Alfred account set, so
               anyone who can see those issues can mark them resolved. Pick the owner's
               account to limit it to them and Super Admins.`
            : `All ${rows.length} categories are claimed — only the owner or a Super Admin
               can resolve.`}
        </div>
      ) : null}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.colCat}>Category</TableCell>
              <TableCell className={classes.colOwner}>Owner</TableCell>
              <TableCell className={classes.colSlack}>
                Slack member ID
              </TableCell>
              <TableCell className={classes.colAccount}>
                Alfred account
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className={classes.empty}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : null}
            {!loading && !rows.length ? (
              <TableRow>
                <TableCell colSpan={4} className={classes.empty}>
                  no categories yet.
                </TableCell>
              </TableRow>
            ) : null}
            {rows.map((row) => {
              const next = draft[row.uuid] || {}
              const sameOwner = rows.filter(
                (other) =>
                  other.uuid !== row.uuid &&
                  (draft[other.uuid]?.owner_name || '').trim().toLowerCase() ===
                    (next.owner_name || '').trim().toLowerCase() &&
                  (next.owner_name || '').trim()
              ).length
              return (
                <TableRow key={row.uuid}>
                  <TableCell className={`${classes.cat} ${classes.wrap}`}>
                    {row.name}
                    {sameOwner ? (
                      <span className={classes.sub}>
                        {next.owner_name} owns {sameOwner} other
                        {sameOwner === 1 ? '' : 's'}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="outlined"
                      className={classes.field}
                      value={next.owner_name || ''}
                      onChange={(event) => {
                        const { value } = event.target
                        set(row.uuid, 'owner_name')(value)
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="outlined"
                      className={classes.field}
                      placeholder="U…"
                      value={next.owner_slack_id || ''}
                      onChange={(event) => {
                        const { value } = event.target
                        set(row.uuid, 'owner_slack_id')(value)
                      }}
                      onBlur={() => applyToSameOwner(row.uuid)}
                    />
                    {!(next.owner_slack_id || '').trim() ? (
                      <span className={classes.missing}> not set</span>
                    ) : null}
                  </TableCell>
                  {/* Who may CLOSE an issue in this category. Deliberately a
                      picker and not typed: the owner name above is a label, and
                      matching it to an account by hand is what gets it wrong —
                      four accounts read "Manjushree Mahadev". Blank is allowed
                      and means anyone who can see the issue may close it. */}
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      className={classes.field}
                      SelectProps={{ native: true }}
                      value={next.owner_user || ''}
                      onChange={(event) => {
                        const { value } = event.target
                        set(row.uuid, 'owner_user')(value)
                      }}
                    >
                      <option value="">anyone can close</option>
                      {people.map((person) => (
                        <option key={person.uuid} value={person.uuid}>
                          {person.label}
                        </option>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={sopOpen}
        onClose={() => setSopOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>How Issue Owners works</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>
            Every issue raised from a centre picks one of these categories, and
            that decides who gets tagged in the centre&apos;s Slack channel. The
            owner is copied onto the issue when it is raised, so changing an
            owner here routes future issues without rewriting old ones.
          </Typography>
          <Typography paragraph>
            <strong>Owner</strong> and <strong>Alfred account</strong> are two
            different things. Owner is the name shown on the issue and in the
            Slack post. Alfred account is the login that may mark issues in this
            category <strong>resolved</strong> — that person, or a Super Admin,
            and nobody else. It is a picker rather than a typed name because
            several people have more than one account under the same name.
          </Typography>
          <Typography paragraph>
            Leaving the account blank is allowed and means anyone who can see
            the issue can close it, which is how the board worked before. Anyone
            can still pick an issue up, move it between categories and reopen it
            — only closing is the owner&apos;s call.
          </Typography>
          <Typography paragraph>
            A blank Slack id is allowed. The message still posts and still names
            the owner — it just cannot tag them. Fill the id in and the next
            issue tags properly; no deploy, no migration.
          </Typography>
          <Typography paragraph>
            Where one person owns several categories, entering their id once
            fills their other empty rows. A category you have deliberately
            pointed somewhere else is never overwritten.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSopOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default IssueOwners
